"use client";

/**
 * PillThread — the composer converse rendered INSIDE the pill, as a thread the
 * pill grows upward to hold (founder 09-01: "the bash icon should go on top
 * even for responses etc"): your line, then Bash's row (his seat on the left,
 * the words beside it, a quiet right part), then the answer under him.
 *
 * The face itself is BashPill's — one Bash that flies between the input line
 * and the seat this row leaves for him. This component renders the seat, the
 * words and the rest; `moodFor` tells the pill which mood the face wears.
 *
 * The engine is unchanged (useComposerConverse); the states are the same ones
 * ComposerInlineCard renders for the other hosts, in the porcelain idiom: no
 * corner bloom, no mono kicker, the task line's copper light on the words.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { ConverseUiState } from "@/components/composer/useConverse";
import { DRAWN_KINDS } from "@/components/composer/panels/drawn";
import { narrationForTool } from "@/components/home-briefing/ComposerInlineCard";
import { labelForModel } from "@/components/ui/model-catalog";
import { useComposerDotsStore } from "@/stores/composerDotsStore";
import { useHistoryStore } from "@/stores/historyStore";
import { useTaskStore } from "@/stores/taskStore";
import {
  STATUS_WORD,
  taskLiveStatus,
  type TaskLiveStatus,
} from "@/lib/task-live-status";
import { renderMarkdown } from "@/lib/markdown";
import type { FaceMood } from "./BashFace";
import { useDockRouter } from "@/components/composer/live/useDockRouter";
import { forwardAction } from "@/lib/dock-window";

interface PillThreadProps {
  /** Never 'idle' when rendered — the host gates on it. */
  ui: ConverseUiState;
  onDismiss: () => void;
  /** Error-state retry — re-sends the ask via the classic task lane. */
  onRetryClassic: () => void;
  /** Credits-state door — the plan page. */
  onUpgrade?: () => void;
  /** The task is leaving: Bash has flown home and the receipt lifts up and
   *  away before the thread folds (the host runs that choreography). */
  lift?: boolean;
  /** Agents first (train 1b): whose row this is. Default 'Bash', so a thread
   *  with nobody picked reads exactly as it reads today. */
  speaker?: string;
}

/** "Saved to Nami" needs no reply — it folds after the confirm beat. */
const NAMI_AUTODISMISS_MS = 2200;

const RECEIPT_SETTLED = new Set<TaskLiveStatus>([
  "done",
  "stopped",
  "out_of_credits",
  "error",
]);

/** Tools that read wear the reading face; everything else is hands-on work. */
const READING_TOOLS = new Set<string>([
  "web_search",
  "web_fetch",
  "fetch_recent_tasks",
  "show_tasks",
  "task_output",
  "task_state",
  "task_creations",
  "task_transcript",
  "recall_from_nami",
  "os_read",
  "os_look",
  "os_ocr",
  "os_selection",
  "os_snapshot",
]);

/** The mood Bash wears on his seat for each phase of the converse. */
export function moodFor(ui: ConverseUiState): FaceMood {
  switch (ui.phase) {
    case "thinking":
      return "thinking";
    case "routing":
      return READING_TOOLS.has(ui.tool) ? "reading" : "working";
    case "answering":
      return "working";
    case "answered":
      return "content";
    case "routed":
      return ui.action.kind === "task_created" ||
        ui.action.kind === "task_followup"
        ? "happy"
        : "content";
    case "error":
    case "credits":
      return "puzzled";
    default:
      return "content";
  }
}

const Chev = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="size-[15px]"
  >
    <path d="m9 6 6 6-6 6" />
  </svg>
);

/** The receipt dot's color per status — the default (queued/running/done/
 *  out_of_credits) stays kind-green; only these three diverge. */
const DOT_BG: Partial<Record<TaskLiveStatus, string>> = {
  needs: "bg-kind-amber",
  stopped: "bg-fg-faint",
  error: "bg-kind-red",
};

export function PillThread({
  ui,
  onDismiss,
  onRetryClassic,
  onUpgrade,
  lift = false,
  speaker = "Bash",
}: PillThreadProps) {
  // In the shell's Dock window a push goes to the main window (useDockRouter).
  const router = useDockRouter();

  const answerText =
    ui.phase === "answered" || ui.phase === "answering" ? ui.text : "";
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    setCopied(false);
  }, [answerText]);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);
  const handleCopy = () => {
    navigator.clipboard
      ?.writeText(answerText)
      .then(() => setCopied(true))
      .catch(() => {
        // Clipboard permission denied — the button simply stays "Copy".
      });
  };

  // A long answer scrolls inside its own box; while streaming, follow the
  // newest text only when the reader is already at the tail.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const streamingText = ui.phase === "answering" ? ui.text : null;
  useEffect(() => {
    if (streamingText === null) return;
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromTail = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromTail < 160) el.scrollTop = el.scrollHeight;
  }, [streamingText]);

  // Esc folds the thread in its settled states, same as the visible ×. The
  // thread CLAIMS the key (preventDefault, window capture) so the Line's own
  // Escape, the Dock's bubble-phase listener yielding to defaultPrevented,
  // leaves the line up when the thread folds.
  const closable =
    ui.phase === "answered" ||
    ui.phase === "error" ||
    ui.phase === "credits" ||
    (ui.phase === "routed" &&
      ui.action.kind === "nami_saved" &&
      Boolean(ui.action.say));
  // A panel the deck DRAWS claims Escape for itself (PanelDeck, window
  // capture, composer panels §3.4): while one is up the thread does not
  // listen at all, so one press closes the panel and the next folds the
  // thread. Registration order must not decide that — both listeners are
  // window CAPTURE listeners, so the one registered first fires first, and a
  // panel arriving after the answer settled registers the deck's last.
  // Yielding to a claimed press could not have saved the thread there. It
  // yields to one anyway (the Line's, a host above): a belt, not the rail.
  const panelClaimsEscape = (ui.panels ?? []).some((p) =>
    DRAWN_KINDS.has(p.kind)
  );
  useEffect(() => {
    if (!closable || panelClaimsEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      onDismiss();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [closable, panelClaimsEscape, onDismiss]);

  const isNami = ui.phase === "routed" && ui.action.kind === "nami_saved";
  // A save that carries its own sentence (a link sent alone) asks them what to
  // do with it, so it stays until they answer or close it.
  const namiAsks =
    ui.phase === "routed" &&
    ui.action.kind === "nami_saved" &&
    Boolean(ui.action.say);
  useEffect(() => {
    if (!isNami || namiAsks) return;
    const t = setTimeout(() => onDismiss(), NAMI_AUTODISMISS_MS);
    return () => clearTimeout(t);
  }, [isNami, namiAsks, onDismiss]);

  // The receipt — status rides the shared truth ladder (live phase → story
  // spine → lifecycle), the same truth the board renders.
  const receipt =
    ui.phase === "routed" &&
    (ui.action.kind === "task_created" || ui.action.kind === "task_followup")
      ? ui.action
      : null;
  const receiptLive = useTaskStore((s) =>
    receipt ? s.tasks.get(receipt.taskId) : undefined
  );
  const receiptRow = useHistoryStore((s) =>
    receipt ? s.taskHistory.find((t) => t.id === receipt.taskId) : undefined
  );
  const receiptStatus: TaskLiveStatus = taskLiveStatus({
    livePhase: receiptLive?.phase,
    liveError: Boolean(receiptLive?.error),
    storyPhase: receiptRow?.storyPhase,
    lifecycleState: receiptRow?.lifecycleState,
  });
  /** The model's own sentence for this request, when it wrote one. */
  const receiptSay =
    receipt && typeof receipt.say === "string" && receipt.say.trim()
      ? receipt.say.trim()
      : null;
  const receiptModel = useMemo(
    () =>
      receipt && receipt.kind === "task_created" && receipt.model
        ? labelForModel(receipt.model)
        : null,
    [receipt]
  );
  const receiptTaskId = receipt?.taskId ?? null;
  const receiptTitle = receipt?.title ?? "";
  const receiptModelId =
    receipt && receipt.kind === "task_created" ? receipt.model : undefined;
  const stampDot = () => {
    if (!receiptTaskId) return;
    useComposerDotsStore.getState().add({
      taskId: receiptTaskId,
      title: receiptTitle,
      model: receiptModelId,
    });
  };

  const busy =
    ui.phase === "thinking" ||
    ui.phase === "routing" ||
    ui.phase === "answering";
  const liveWords =
    ui.phase === "thinking"
      ? "Thinking"
      : ui.phase === "routing"
        ? narrationForTool(ui.tool)
        : null;

  const brightBtn =
    "bh-bright__btn transition-bh-hover rounded-lg bg-transparent border-0 py-1 px-1.5 font-inherit text-3.5 text-fg-faint cursor-pointer hover:text-fg-default hover:bg-bg-muted";
  const brightIco =
    "bh-bright__ico transition-bh-hover relative grid size-8 place-items-center rounded-[10px] border-0 bg-transparent p-0 text-fg-muted cursor-pointer hover:text-fg-default hover:bg-bg-muted after:content-[attr(data-tip)] after:absolute after:top-[calc(100%+6px)] after:left-1/2 after:z-2 after:-translate-x-1/2 after:rounded-full after:bg-fg-default after:px-2.5 after:py-1.5 after:text-3 after:font-medium after:leading-[1.2] after:whitespace-nowrap after:text-bg-base after:opacity-0 after:pointer-events-none after:transition-bh-fade hover:after:opacity-100 focus-visible:after:opacity-100";
  const answerBox =
    "bh-answer text-4 leading-[1.6] text-fg-default pt-1 pr-2 pb-3 pl-11 max-h-[min(40vh,320px)] overflow-y-auto overscroll-contain";
  const tfootCopy =
    "bh-tfoot__copy transition-bh-hover ml-auto rounded-full border border-border-default bg-bg-elevated py-1.5 px-3 font-inherit text-3.5 text-fg-muted cursor-pointer hover:bg-bg-muted hover:text-fg-default";
  const tfootCopyPrimary =
    "bh-tfoot__copy--primary bg-fg-default border-fg-default text-fg-inverse";
  // the task going to Recents: once Bash is home the receipt row hands
  // itself to a ghost card (taskFlight.ts) that flies to its row; the row
  // in the pill just goes quiet
  const browLift = cn("transition-bh-brow-fade", lift && "opacity-0");

  return (
    <div
      className={cn(
        "bh-thread min-h-0 overflow-hidden px-4 pt-3.5",
        lift && "bh-thread--lift"
      )}
      data-testid="bh-thread"
      role="status"
    >
      {/* Bash's row: the seat he lands on, the words, the right part. Your own
          words are not repeated here (founder 09-01): you just typed them. */}
      {receipt ? (
        <button
          type="button"
          className={cn(
            "bh-brow bh-brow--receipt group transition-bh-brow-bg relative flex min-h-11 w-[calc(100%+12px)] -mx-1.5 items-center gap-3 rounded-[14px] border-0 bg-transparent py-1.5 pr-2.5 pl-1.5 text-left text-inherit [font:inherit] cursor-pointer hover:bg-bg-muted",
            browLift
          )}
          aria-label={`Open task: ${receipt.title}`}
          onClick={() => {
            stampDot();
            router.push(`/task/${receipt.taskId}`);
            onDismiss();
          }}
        >
          <span
            className="bh-seat inline-block h-8 w-8 flex-none"
            data-seat="top"
            data-testid="bh-seat-top"
          />
          <span className="bh-bw animate-bh-words-in min-w-0 flex-1 truncate text-4 text-fg-default">
            {/* The sentence is the row (founder mock 09-02, option C). Same
                rule as the inline card: when the model wrote a line for this
                request it IS the row, and the "Task created" label goes with
                the title it used to sit above. Without one, the row is
                exactly what it was. */}
            {receiptSay ? (
              <span className="bh-bw__say font-normal">{receiptSay}</span>
            ) : (
              <b className="font-medium">{receipt.title}</b>
            )}
            <span
              className="bh-bw__meta mt-px flex items-center gap-1.75 text-3.5 font-normal text-fg-subtle"
              data-testid="bh-receipt-meta"
            >
              {!receiptSay &&
                (receipt.kind === "task_created"
                  ? "Task created"
                  : "Task updated")}
              <span
                className={cn(
                  "bh-bw__dot size-1.75 rounded-full",
                  DOT_BG[receiptStatus] ?? "bg-kind-green",
                  RECEIPT_SETTLED.has(receiptStatus)
                    ? "bh-bw__dot--still"
                    : "animate-bh-dot-pulse"
                )}
                data-status={receiptStatus}
                aria-hidden
              />
              {STATUS_WORD[receiptStatus]}
              {receiptModel && <> · {receiptModel}</>} · just now
            </span>
          </span>
          <span className="bh-bright flex-none ml-auto inline-flex items-center gap-2.5 text-3.5 text-fg-faint">
            <span className="bh-bright__go transition-bh-hover grid size-7 place-items-center rounded-full border border-border-subtle bg-bg-muted text-fg-muted group-hover:bg-border-default group-hover:text-fg-default">
              <Chev />
            </span>
          </span>
        </button>
      ) : (
        <div
          className={cn(
            "bh-brow transition-bh-brow-bg relative flex min-h-11 w-[calc(100%+12px)] -mx-1.5 items-center gap-3 rounded-[14px] border-0 bg-transparent py-1.5 pr-2.5 pl-1.5 text-left text-inherit [font:inherit]",
            browLift
          )}
        >
          <span
            className="bh-seat inline-block h-8 w-8 flex-none"
            data-seat="top"
            data-testid="bh-seat-top"
          />
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-4 text-fg-default",
              liveWords
                ? "bh-bw bh-bw--live animate-bh-words-in-shimmer bg-bh-live-shimmer bg-[length:220%_100%] bg-clip-text font-normal text-transparent"
                : "bh-bw animate-bh-words-in"
            )}
            data-testid="bh-words"
          >
            {liveWords ?? (
              <b className="font-medium">
                {ui.phase === "error"
                  ? "Not sent"
                  : ui.phase === "credits"
                    ? "Out of credits"
                    : isNami
                      ? "Saved to Nami"
                      : speaker}
              </b>
            )}
          </span>
          <span className="bh-bright flex-none ml-auto inline-flex items-center gap-2.5 text-3.5 text-fg-faint">
            {busy && (
              <button type="button" className={brightBtn} onClick={onDismiss}>
                Stop
              </button>
            )}
            {ui.phase === "answered" && (
              <>
                {/* Copy and close ride Bash's row as two icons (founder 09-01). */}
                <button
                  type="button"
                  className={brightIco}
                  onClick={handleCopy}
                  aria-label={copied ? "Copied" : "Copy"}
                  data-tip={copied ? "Copied" : "Copy"}
                >
                  {copied ? (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      className="size-4.5"
                    >
                      <path d="m5 12.5 4.5 4.5L19 7" />
                    </svg>
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                      className="size-4.5"
                    >
                      <rect x="3" y="8" width="13" height="13" rx="3" />
                      <path d="M8 8V6a3 3 0 0 1 3-3h7a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3h-2" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  className={cn(brightIco, "-ml-1.5")}
                  onClick={onDismiss}
                  aria-label="Close"
                  data-tip="Close"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    aria-hidden="true"
                    className="size-4.5"
                  >
                    <path d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              </>
            )}
          </span>
        </div>
      )}

      {/* The answer, under Bash, indented to his words */}
      {(ui.phase === "answering" || ui.phase === "answered") && (
        <div
          className={answerBox}
          ref={scrollRef}
          data-testid="bh-answer"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(ui.text) }}
        />
      )}
      {isNami && ui.phase === "routed" && ui.action.kind === "nami_saved" && (
        <>
          <div className={answerBox} data-testid="bh-nami">
            {ui.action.say || "Filed. Nothing to do"}
            {ui.action.summary && (
              <span className="bh-answer__sub block text-3.5 text-fg-subtle">
                {ui.action.summary}
              </span>
            )}
          </div>
          {ui.action.say && (
            <div className="bh-tfoot flex items-center gap-2.5 pt-1.5 pr-2 pb-2.5 pl-11 text-3.5 text-fg-subtle border-b border-border-subtle">
              <button type="button" className={brightBtn} onClick={onDismiss}>
                Done
              </button>
            </div>
          )}
        </>
      )}

      {/* The connect link (2a): a tap target on Bash's row; the tab opened on its own where the
          browser allowed it. The same https belt the opener uses. */}
      {ui.phase === "routed" &&
        ui.action.kind === "open_url" &&
        /^https:\/\//.test(ui.action.url) && (
          <div className={answerBox} data-testid="bh-open-url">
            <a
              href={ui.action.url}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              // The shell's Dock window: the main window opens it (the system browser).
              onClick={(e) => {
                if (forwardAction(ui.action)) e.preventDefault();
              }}
            >
              {ui.action.label}
            </a>
          </div>
        )}

      {/* Out of credits (founder 09-02): the thread used to go BLANK on this
          state. The door is Upgrade, never a retry the server would refuse. */}
      {ui.phase === "credits" && (
        <>
          <div className={answerBox}>{ui.message}</div>
          <div className="bh-tfoot flex items-center gap-2.5 pt-1.5 pr-2 pb-2.5 pl-11 text-3.5 text-fg-subtle border-b border-border-subtle">
            <button type="button" className={brightBtn} onClick={onDismiss}>
              Dismiss
            </button>
            <button
              type="button"
              className={cn(tfootCopy, tfootCopyPrimary)}
              onClick={onUpgrade}
            >
              Upgrade
            </button>
          </div>
        </>
      )}

      {ui.phase === "error" && (
        <>
          <div className={answerBox}>
            That didn&apos;t go through. Retry runs it as a task instead.
          </div>
          <div className="bh-tfoot flex items-center gap-2.5 pt-1.5 pr-2 pb-2.5 pl-11 text-3.5 text-fg-subtle border-b border-border-subtle">
            <button type="button" className={brightBtn} onClick={onDismiss}>
              Dismiss
            </button>
            <button
              type="button"
              className={cn(tfootCopy, tfootCopyPrimary)}
              onClick={onRetryClassic}
            >
              Retry as a task
            </button>
          </div>
        </>
      )}
    </div>
  );
}
